import { ref, shallowRef, triggerRef, type Ref, type ShallowRef } from 'vue'
import { useApiCredentials } from '@/composables/useApiCredentials'
import { useAuthorizationStore } from '@/stores/AuthorizationStore/AuthorizationStore'
import { getDevSimulatorHeaders } from '@/util/devSimulatorHeaders'

/**
 * Event types emitted by the agent streaming endpoint.
 * Mirrors ``AgentStreamEventType`` from kmlib.
 */
export type AgentStreamEventType =
  | 'agent_user_prompt'
  | 'agent_model_request'
  | 'agent_thinking'
  | 'agent_text_delta'
  | 'agent_tool_call_start'
  | 'agent_tool_call_args'
  | 'agent_tool_result'
  | 'agent_part_start'
  | 'agent_part_end'
  | 'agent_final_result'
  | 'agent_code_execution'
  | 'agent_completed'
  | 'agent_error'
  | 'agent_subagent_start'
  | 'agent_subagent_end'

/**
 * Cost information from a completed agent run.
 */
export interface CostEntryInfo {
  provider: string
  model: string
  input_tokens: number
  output_tokens: number
  total_usd: number
  input_usd: number
  output_usd: number
  operation: string
}

/**
 * A single SSE event from the agent stream.
 * Mirrors ``AgentStreamEvent`` from kmlib.
 */
export interface AgentStreamEvent {
  event_type: AgentStreamEventType
  content: string
  tool_name: string
  tool_call_id: string
  tool_args: Record<string, unknown>
  is_final: boolean
  agent_name?: string
  usage?: Record<string, unknown> | null
  cost_entry?: CostEntryInfo | null
  citations?: string[] | null
  result?: unknown
  history?: unknown[]
  part?: Record<string, unknown> | null
}

export interface UseSSEReturn {
  /** Whether a stream is currently active. */
  streaming: Ref<boolean>
  /** Current execution run identifier, when known. */
  runId: Ref<string | null>
  /** Accumulated text content from text_delta events. */
  streamedText: Ref<string>
  /** Accumulated thinking content from thinking_delta events. */
  streamedThinking: Ref<string>
  /** Active tool calls being processed. */
  activeToolCalls: Ref<ToolCallInfo[]>
  /** The last error message, if any. */
  error: Ref<string | null>
  /** The final usage stats from the completed event. */
  usage: Ref<Record<string, unknown> | null>
  /** Cost entry from the completed event. */
  costEntry: Ref<CostEntryInfo | null>
  /** Citations from the completed event (Perplexity). */
  citations: Ref<string[] | null>
  /** Final result from the completed event. */
  result: Ref<unknown>
  /** Message history from the completed event (pydantic-ai format). */
  history: Ref<unknown[]>
  /** Active agent-definition activities (thinking, text, tools per agent-definition). */
  subAgentActivities: ShallowRef<SubAgentActivity[]>
  /** Ordered conversation flow for coordinator and sub-agent events. */
  conversationTimeline: ShallowRef<ConversationTimelineItem[]>
  /** Live task list built from planning tool events. */
  todoItems: Ref<TodoLiveItem[]>
  /** Start streaming from the agent endpoint. */
  startStream: (url: string, body: unknown) => Promise<void>
  /** Reconnect to an existing agent stream. */
  resumeStream: (url: string) => Promise<void>
  /** Abort the current stream. */
  abort: () => void
}

export interface ToolCallInfo {
  id: string
  name: string
  args: string
  result?: string
}

/**
 * Tracks the real-time activity of a single agent-definition during delegation.
 * Updated live as streaming events arrive.
 */
export interface SubAgentActivity {
  /** Name of the agent-definition. */
  name: string
  /** Whether the agent-definition has finished. */
  done: boolean
  /** Accumulated text output. */
  text: string
  /** Accumulated thinking content. */
  thinking: string
  /** Tool calls made by this agent-definition. */
  toolCalls: ToolCallInfo[]
  /** Error message if the agent-definition failed. */
  error?: string
  /** Whether the agent-definition is currently in a thinking part. */
  isThinking?: boolean
}

export interface ConversationTimelineItem {
  id: string
  type: 'coordinator' | 'subagent'
  text: string
  done: boolean
  name?: string
  toolNames: string[]
  error?: string
  thinking?: string
  isThinking?: boolean
  /** Eagerly captured delegation info so it survives event batching. */
  delegation?: { mention: string; body: string }
  /** Delegate task call bound to this coordinator bubble. */
  delegationToolCallId?: string
}

export type TodoLiveStatus = 'not_started' | 'in_progress' | 'completed' | 'blocked' | 'cancelled'

export interface TodoLiveItem {
  id: number
  title: string
  status: TodoLiveStatus
}

/**
 * Composable for consuming Server-Sent Events from the agent streaming
 * endpoint.  Uses ``fetch()`` instead of ``EventSource`` to support
 * ``POST`` requests with custom ``Authorization`` headers.
 */
export function useSSE(): UseSSEReturn {
  const { axiosBearerHeader, bearerToken, guid } = useApiCredentials()
  const authStore = useAuthorizationStore()

  const streaming = ref(false)
  const streamedText = ref('')
  const streamedThinking = ref('')
  const activeToolCalls: Ref<ToolCallInfo[]> = ref([])
  const error: Ref<string | null> = ref(null)
  const usage: Ref<Record<string, unknown> | null> = ref(null)
  const costEntry: Ref<CostEntryInfo | null> = ref(null)
  const citations: Ref<string[] | null> = ref(null)
  const result: Ref<unknown> = ref(null)
  const history: Ref<unknown[]> = ref([])
  const subAgentActivities: ShallowRef<SubAgentActivity[]> = shallowRef([])
  const conversationTimeline: ShallowRef<ConversationTimelineItem[]> = shallowRef([])
  const todoItems: Ref<TodoLiveItem[]> = ref([])

  const todoStartToolCallIds = new Set<string>()

  let controller: AbortController | null = null
  /** The current execution run ID (from ``X-Run-Id`` response header). */
  const runId: Ref<string | null> = ref(null)
  /** Cancel endpoint for the current run. */
  let _cancelUrl: string | null = null

  /**
   * Reset all reactive state before a new stream.
   */
  function reset() {
    streamedText.value = ''
    streamedThinking.value = ''
    activeToolCalls.value = []
    error.value = null
    usage.value = null
    costEntry.value = null
    citations.value = null
    result.value = null
    history.value = []
    subAgentActivities.value = []
    conversationTimeline.value = []
    todoItems.value = []
    todoStartToolCallIds.clear()
    runId.value = null
    _cancelUrl = null
  }

  /**
   * Abort the current stream and request backend cancellation.
   */
  function abort() {
    const cancelUrl = _cancelUrl
    controller?.abort()
    controller = null
    streaming.value = false
    if (cancelUrl) {
      _cancelExecution(cancelUrl)
    }
  }

  /**
   * Fire-and-forget POST to cancel the agent execution on the backend.
   */
  function _cancelExecution(cancelUrl: string) {
    fetch(cancelUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...axiosBearerHeader.value.headers,
        ...getDevSimulatorHeaders(),
      },
      credentials: 'include',
    }).catch(() => { /* best-effort */ })
  }

  function extractRunIdFromStreamUrl(url: string): string | null {
    const match = url.match(/\/agent\/([a-f0-9-]{36})\/stream(?:\?.*)?$/i)
    return match?.[1] || null
  }

  function deriveCancelUrl(url: string, currentRunId: string | null): string | null {
    if (/\/agent\/stream(?:\?.*)?$/i.test(url)) {
      return currentRunId ? url.replace(/\/stream(?:\?.*)?$/i, `/${currentRunId}/cancel`) : null
    }
    if (/\/agent\/[a-f0-9-]{36}\/stream(?:\?.*)?$/i.test(url)) {
      return url.replace(/\/stream(?:\?.*)?$/i, '/cancel')
    }
    return null
  }

  async function readErrorDetail(response: Response): Promise<string> {
    try {
      const errBody = await response.json()
      if (errBody?.detail) {
        return typeof errBody.detail === 'string' ? errBody.detail : JSON.stringify(errBody.detail)
      }
    } catch {
      // Response body is optional on failures.
    }
    return response.statusText
  }

  async function retryWithFreshToken(): Promise<boolean> {
    if (!guid.value) {
      return false
    }

    if (bearerToken.value?.refresh_token) {
      await authStore.refresh_token(guid.value, false)
      return true
    }

    await authStore.aquire_token(guid.value, false)
    return true
  }

  async function openStream(
    url: string,
    buildInit: () => RequestInit,
    initialRunId: string | null = null,
  ): Promise<void> {
    abort()
    reset()
    streaming.value = true
    controller = new AbortController()
    runId.value = initialRunId
    _cancelUrl = deriveCancelUrl(url, initialRunId)

    // Pre-create a coordinator bubble immediately so the user sees typing
    // dots right away while the worker picks up the task.
    getOrCreateCoordinatorTimelineItem()

    try {
      let response: Response
      let retriedAfterRefresh = false

      while (true) {
        response = await fetch(url, {
          ...buildInit(),
          signal: controller.signal,
          credentials: 'include',
        })

        if (response.ok) {
          break
        }

        const detail = await readErrorDetail(response)
        if (
          !retriedAfterRefresh
          && response.status === 401
          && detail === 'Signature has expired'
        ) {
          retriedAfterRefresh = await retryWithFreshToken()
          if (retriedAfterRefresh) {
            continue
          }
        }

        error.value = `HTTP ${response.status}: ${detail}`
        streaming.value = false
        return
      }

      runId.value = response.headers.get('X-Run-Id') || runId.value
      _cancelUrl = deriveCancelUrl(url, runId.value)

      const reader = response.body?.getReader()
      if (!reader) {
        error.value = 'No readable stream in response'
        streaming.value = false
        return
      }

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })

        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed || !trimmed.startsWith('data: ')) continue

          const jsonStr = trimmed.slice(6)
          try {
            const event: AgentStreamEvent = JSON.parse(jsonStr)
            handleEvent(event)
          } catch {
            // Skip malformed JSON lines
          }
        }
      }

      if (buffer.trim().startsWith('data: ')) {
        try {
          const event: AgentStreamEvent = JSON.parse(buffer.trim().slice(6))
          handleEvent(event)
        } catch {
          // Skip malformed tail
        }
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // User-initiated abort — not an error
      } else {
        error.value = err instanceof Error ? err.message : String(err)
      }
    } finally {
      streaming.value = false
      controller = null
    }
  }

  /**
   * Start a POST-based SSE stream to ``url`` with the given ``body``.
   *
   * The function reads the response as a text stream line-by-line, parses
   * SSE ``data:`` lines into ``AgentStreamEvent`` objects, and updates the
   * reactive refs accordingly.
   */
  async function startStream(url: string, body: unknown): Promise<void> {
    const requestBody = JSON.stringify(body)

    await openStream(url, () => ({
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...axiosBearerHeader.value.headers,
        ...getDevSimulatorHeaders(),
      },
      body: requestBody,
    }))
  }

  async function resumeStream(url: string): Promise<void> {
    await openStream(
      url,
      () => ({
        method: 'GET',
        headers: {
          ...axiosBearerHeader.value.headers,
          ...getDevSimulatorHeaders(),
        },
      }),
      extractRunIdFromStreamUrl(url),
    )
  }

  /**
   * Get or create a SubAgentActivity entry for the given agent name.
   */
  function getOrCreateActivity(agentName: string): SubAgentActivity {
    let activity = subAgentActivities.value.find((a) => a.name === agentName && !a.done)
    if (!activity) {
      activity = {
        name: agentName,
        done: false,
        text: '',
        thinking: '',
        toolCalls: [],
        isThinking: false,
      }
      subAgentActivities.value.push(activity)
    }
    return activity
  }

  function isThinkingPart(event: AgentStreamEvent): boolean {
    const part = event.part
    if (!part || typeof part !== 'object') return false

    const partKind = [part.part_kind, part.kind, part.type]
      .find((value): value is string => typeof value === 'string')

    return !!partKind && partKind.toLowerCase().includes('thinking')
  }

  function createTimelineId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  }

  function normalizeAgentName(value: string): string {
    return value.trim().replace(/^@+/, '').toLowerCase()
  }

  function firstStringValue(obj: Record<string, unknown>, keys: string[]): string | null {
    for (const key of keys) {
      const candidate = obj[key]
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim()
      }
    }
    return null
  }

  function decodeJsonEscapedValue(value: string): string {
    const escaped = value.replaceAll('\\', '\\\\').replaceAll('"', '\\"')
    try {
      return JSON.parse(`"${escaped}"`) as string
    } catch {
      return value
    }
  }

  function extractStringField(rawArgs: string, fieldName: string): string | null {
    if (!rawArgs || !fieldName) return null
    const fieldPattern = new RegExp(`"${fieldName}"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"`, 'i')
    const match = rawArgs.match(fieldPattern)
    if (!match || !match[1]) return null
    const decoded = decodeJsonEscapedValue(match[1]).trim()
    return decoded || null
  }

  function safeParseToolArgs(rawArgs: string): Record<string, unknown> | null {
    if (!rawArgs.trim()) return null
    try {
      const parsed = JSON.parse(rawArgs)
      return parsed && typeof parsed === 'object' ? parsed as Record<string, unknown> : null
    } catch {
      return null
    }
  }

  function extractDelegationDetails(rawArgs: string): { agentName: string | null; taskText: string | null } {
    const parsed = safeParseToolArgs(rawArgs)
    const agentName =
      (parsed ? firstStringValue(parsed, ['agent_name', 'agent', 'target_agent', 'subagent', 'coworker']) : null)
      || extractStringField(rawArgs, 'agent_name')
      || extractStringField(rawArgs, 'agent')
      || extractStringField(rawArgs, 'target_agent')
      || extractStringField(rawArgs, 'subagent')
      || extractStringField(rawArgs, 'coworker')
    const taskText =
      (parsed ? firstStringValue(parsed, ['task', 'prompt', 'instruction', 'message', 'query']) : null)
      || extractStringField(rawArgs, 'task')
      || extractStringField(rawArgs, 'prompt')
      || extractStringField(rawArgs, 'instruction')
      || extractStringField(rawArgs, 'message')
      || extractStringField(rawArgs, 'query')
    return { agentName, taskText }
  }

  function findCoordinatorTimelineItemByToolCallId(toolCallId: string): ConversationTimelineItem | null {
    if (!toolCallId) return null
    for (let index = conversationTimeline.value.length - 1; index >= 0; index -= 1) {
      const item = conversationTimeline.value[index]
      if (item.type === 'coordinator' && item.delegationToolCallId === toolCallId) {
        return item
      }
    }
    return null
  }

  function applyDelegationToCoordinatorItem(
    item: ConversationTimelineItem | null,
    agentName: string,
    taskText?: string | null,
  ): void {
    if (!item) return
    const cleanAgentName = agentName.trim()
    if (!cleanAgentName) return
    item.delegation = {
      mention: `@${cleanAgentName.replace(/^@+/, '')}`,
      body: taskText && taskText.trim() ? taskText.trim() : 'arbeitet ...',
    }
    item.done = true
  }

  function getPendingCoordinatorTimelineItem(agentName?: string): ConversationTimelineItem | null {
    const normalizedName = agentName ? normalizeAgentName(agentName) : ''

    for (const item of conversationTimeline.value) {
      if (item.type !== 'coordinator' || item.done || !item.delegationToolCallId || !item.delegation) {
        continue
      }
      if (normalizedName && normalizeAgentName(item.delegation.mention) === normalizedName) {
        return item
      }
    }

    for (const item of conversationTimeline.value) {
      if (item.type === 'coordinator' && !item.done && item.delegationToolCallId) {
        return item
      }
    }

    return null
  }

  function getOrCreateCoordinatorTimelineItem(toolCallId?: string): ConversationTimelineItem {
    if (toolCallId) {
      const existing = findCoordinatorTimelineItemByToolCallId(toolCallId)
      if (existing) {
        return existing
      }

      const lastItem = conversationTimeline.value[conversationTimeline.value.length - 1]
      if (lastItem && lastItem.type === 'coordinator' && !lastItem.done && !lastItem.delegationToolCallId) {
        lastItem.delegationToolCallId = toolCallId
        return lastItem
      }

      const nextItem: ConversationTimelineItem = {
        id: createTimelineId('coord'),
        type: 'coordinator',
        text: '',
        done: false,
        toolNames: [],
        delegationToolCallId: toolCallId,
      }
      conversationTimeline.value.push(nextItem)
      return nextItem
    }

    const lastItem = conversationTimeline.value[conversationTimeline.value.length - 1]
    if (lastItem && lastItem.type === 'coordinator' && !lastItem.done && !lastItem.delegationToolCallId) {
      return lastItem
    }

    const nextItem: ConversationTimelineItem = {
      id: createTimelineId('coord'),
      type: 'coordinator',
      text: '',
      done: false,
      toolNames: [],
    }
    conversationTimeline.value.push(nextItem)
    return nextItem
  }

  function getActiveSubAgentTimelineItem(agentName: string): ConversationTimelineItem | null {
    for (let index = conversationTimeline.value.length - 1; index >= 0; index -= 1) {
      const item = conversationTimeline.value[index]
      if (item.type === 'subagent' && item.name === agentName && !item.done) {
        return item
      }
    }
    return null
  }

  function getOrCreateSubAgentTimelineItem(agentName: string): ConversationTimelineItem {
    const existing = getActiveSubAgentTimelineItem(agentName)
    if (existing) {
      return existing
    }

    const nextItem: ConversationTimelineItem = {
      id: createTimelineId(`sub-${agentName}`),
      type: 'subagent',
      name: agentName,
      text: '',
      done: false,
      toolNames: [],
    }
    conversationTimeline.value.push(nextItem)
    return nextItem
  }

  function appendToolName(item: ConversationTimelineItem | null, toolName: string): void {
    if (!item || !toolName) return
    if (!item.toolNames.includes(toolName)) {
      item.toolNames.push(toolName)
    }
  }

  function isTodoPlanningTool(toolName: string): boolean {
    return toolName === 'write_todos'
      || toolName === 'add_todo'
      || toolName === 'update_todo_status'
      || toolName === 'remove_todo'
  }

  function parseTodoStatus(value: unknown): TodoLiveStatus {
    if (value === 'in_progress') return 'in_progress'
    if (value === 'completed') return 'completed'
    if (value === 'blocked') return 'blocked'
    if (value === 'cancelled') return 'cancelled'
    return 'not_started'
  }

  function parseTodoId(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return Math.max(1, Math.floor(value))
    }
    if (typeof value === 'string') {
      const parsed = Number.parseInt(value, 10)
      if (Number.isFinite(parsed)) {
        return Math.max(1, parsed)
      }
    }
    return null
  }

  function nextTodoId(): number {
    return todoItems.value.reduce((maxId, item) => Math.max(maxId, item.id), 0) + 1
  }

  function sortTodos(items: TodoLiveItem[]): TodoLiveItem[] {
    return [...items].sort((left, right) => left.id - right.id)
  }

  function applyWriteTodos(toolArgs: Record<string, unknown>): void {
    const rawItems = toolArgs.items
    if (!Array.isArray(rawItems)) return

    const nextItems: TodoLiveItem[] = []
    let fallbackId = 1

    for (const rawItem of rawItems) {
      if (!rawItem || typeof rawItem !== 'object') continue
      const item = rawItem as Record<string, unknown>
      const title = typeof item.title === 'string' ? item.title.trim() : ''
      if (!title) continue

      const parsedId = parseTodoId(item.id)
      const id = parsedId ?? fallbackId
      fallbackId = Math.max(fallbackId, id + 1)

      nextItems.push({
        id,
        title,
        status: parseTodoStatus(item.status),
      })
    }

    todoItems.value = sortTodos(nextItems)
  }

  function applyAddTodo(toolArgs: Record<string, unknown>): void {
    const title = typeof toolArgs.title === 'string' ? toolArgs.title.trim() : ''
    if (!title) return

    const id = parseTodoId(toolArgs.id) ?? nextTodoId()
    const existingIndex = todoItems.value.findIndex((item) => item.id === id)
    if (existingIndex >= 0) {
      const updated = [...todoItems.value]
      updated[existingIndex] = {
        ...updated[existingIndex],
        title,
        status: parseTodoStatus(toolArgs.status),
      }
      todoItems.value = sortTodos(updated)
      return
    }

    todoItems.value = sortTodos([
      ...todoItems.value,
      {
        id,
        title,
        status: parseTodoStatus(toolArgs.status),
      },
    ])
  }

  function applyUpdateTodoStatus(toolArgs: Record<string, unknown>): void {
    const itemId = parseTodoId(toolArgs.item_id)
    if (itemId == null) return

    const itemIndex = todoItems.value.findIndex((item) => item.id === itemId)
    if (itemIndex < 0) return

    const updated = [...todoItems.value]
    updated[itemIndex] = {
      ...updated[itemIndex],
      status: parseTodoStatus(toolArgs.new_status),
    }
    todoItems.value = updated
  }

  function applyRemoveTodo(toolArgs: Record<string, unknown>): void {
    const itemId = parseTodoId(toolArgs.item_id)
    if (itemId == null) return
    todoItems.value = todoItems.value.filter((item) => item.id !== itemId)
  }

  function applyTodoMutation(toolName: string, toolArgs: Record<string, unknown>, toolCallId: string): void {
    if (!isTodoPlanningTool(toolName)) return

    // The stream can emit duplicate start events per tool-call ID.
    if (toolCallId && todoStartToolCallIds.has(toolCallId)) {
      return
    }
    if (toolCallId) {
      todoStartToolCallIds.add(toolCallId)
    }

    if (toolName === 'write_todos') {
      applyWriteTodos(toolArgs)
      return
    }
    if (toolName === 'add_todo') {
      applyAddTodo(toolArgs)
      return
    }
    if (toolName === 'update_todo_status') {
      applyUpdateTodoStatus(toolArgs)
      return
    }
    if (toolName === 'remove_todo') {
      applyRemoveTodo(toolArgs)
    }
  }

  function parseTodosFromPromptText(content: string): TodoLiveItem[] {
    const trimmed = content.trim()
    if (!trimmed || trimmed === 'No tasks in the todo list.') {
      return []
    }

    const parsedItems: TodoLiveItem[] = []
    const iconStatusMap: Record<string, TodoLiveStatus> = {
      ' ': 'not_started',
      '~': 'in_progress',
      x: 'completed',
      '!': 'blocked',
      '-': 'cancelled',
    }

    for (const rawLine of content.split('\n')) {
      const line = rawLine.trim()
      const match = line.match(/^\[([ ~x!\-])\]\s+#(\d+):\s+(.+)$/)
      if (!match) continue

      const statusToken = match[1]
      const id = Number.parseInt(match[2], 10)
      if (!Number.isFinite(id)) continue

      let title = match[3].trim()
      title = title.replace(/\s+\[depends:\s*[^\]]+\]\s*$/i, '')
      title = title.replace(/\s+\(P\d+\)\s*$/i, '')

      parsedItems.push({
        id,
        title,
        status: iconStatusMap[statusToken] ?? 'not_started',
      })
    }

    return sortTodos(parsedItems)
  }

  function applyTodoResult(toolName: string, resultContent: string): void {
    if (toolName !== 'read_todos') return
    const parsed = parseTodosFromPromptText(resultContent)
    todoItems.value = parsed
  }

  /**
   * Process a single ``AgentStreamEvent``.
   */
  function handleEvent(event: AgentStreamEvent) {
    const agentName = event.agent_name || ''

    // Agent definition events (agent_name is set)
    if (agentName) {
      const activity = getOrCreateActivity(agentName)

      switch (event.event_type) {
        case 'agent_subagent_start': {
          const fallbackCoordinatorItem = getPendingCoordinatorTimelineItem(agentName)
            || conversationTimeline.value[conversationTimeline.value.length - 1]
          if (fallbackCoordinatorItem && fallbackCoordinatorItem.type === 'coordinator' && !fallbackCoordinatorItem.done) {
            // Fallback: attach delegation if not already set by
            // agent_tool_call_start (e.g. first start event had no args).
            if (!fallbackCoordinatorItem.delegation) {
              applyDelegationToCoordinatorItem(
                fallbackCoordinatorItem,
                agentName,
                event.content?.trim() || 'arbeitet ...',
              )
            } else {
              fallbackCoordinatorItem.done = true
            }
          }
          // Merge consecutive delegations to the same sub-agent into one
          // continuous bubble when no coordinator text appeared in between.
          let reopened = false
          for (let i = conversationTimeline.value.length - 1; i >= 0; i -= 1) {
            const prev = conversationTimeline.value[i]
            if (prev.type === 'subagent' && prev.name === agentName && prev.done) {
              prev.done = false
              reopened = true
              break
            }
            if (prev.type === 'coordinator' && prev.text?.trim()) {
              break
            }
          }
          if (!reopened) {
            getOrCreateSubAgentTimelineItem(agentName)
          }
          break
        }
        case 'agent_subagent_end': {
          activity.done = true
          activity.isThinking = false
          const endItem = getOrCreateSubAgentTimelineItem(agentName)
          endItem.done = true
          endItem.isThinking = false
          break
        }
        case 'agent_part_start': {
          const subItem = getOrCreateSubAgentTimelineItem(agentName)
          if (isThinkingPart(event)) {
            activity.isThinking = true
            subItem.isThinking = true
            if (event.content) {
              activity.thinking += event.content
              subItem.thinking = (subItem.thinking || '') + event.content
            }
          } else {
            activity.isThinking = false
            subItem.isThinking = false
            if (event.content) {
              activity.text += event.content
              subItem.text += event.content
            }
          }
          break
        }
        case 'agent_part_end':
          if (isThinkingPart(event)) {
            activity.isThinking = false
            getOrCreateSubAgentTimelineItem(agentName).isThinking = false
          }
          break
        case 'agent_text_delta':
          activity.text += event.content
          getOrCreateSubAgentTimelineItem(agentName).text += event.content
          break
        case 'agent_thinking': {
          activity.thinking += event.content
          const thinkItem = getOrCreateSubAgentTimelineItem(agentName)
          thinkItem.thinking = (thinkItem.thinking || '') + event.content
          break
        }
        case 'agent_tool_call_start': {
          const existing = activity.toolCalls.find((t) => t.id === event.tool_call_id)
          if (existing) {
            if (event.tool_args && Object.keys(event.tool_args).length > 0) {
              existing.args = JSON.stringify(event.tool_args, null, 2)
            }
          } else {
            activity.toolCalls.push({
              id: event.tool_call_id,
              name: event.tool_name,
              args: JSON.stringify(event.tool_args, null, 2),
            })
          }
          appendToolName(getOrCreateSubAgentTimelineItem(agentName), event.tool_name)
          applyTodoMutation(event.tool_name || '', event.tool_args || {}, event.tool_call_id || '')
          break
        }
        case 'agent_tool_call_args': {
          let tc = activity.toolCalls.find((t) => t.id === event.tool_call_id)
          if (!tc) {
            tc = {
              id: event.tool_call_id,
              name: event.tool_name || 'tool',
              args: '',
            }
            activity.toolCalls.push(tc)
            appendToolName(getOrCreateSubAgentTimelineItem(agentName), tc.name)
          }
          tc.args += event.content
          break
        }
        case 'agent_tool_result': {
          let tc = activity.toolCalls.find((t) => t.id === event.tool_call_id)
          if (!tc) {
            tc = {
              id: event.tool_call_id,
              name: event.tool_name || 'tool',
              args: '',
            }
            activity.toolCalls.push(tc)
            appendToolName(getOrCreateSubAgentTimelineItem(agentName), tc.name)
          }
          tc.result = event.content
          applyTodoResult(tc.name || event.tool_name || '', event.content || '')
          break
        }
        case 'agent_error':
          activity.error = event.content
          getOrCreateSubAgentTimelineItem(agentName).error = event.content
          break
        default:
          break
      }
      triggerRef(subAgentActivities)
      triggerRef(conversationTimeline)
      return
    }

    // Parent agent events (no agent_name)
    switch (event.event_type) {
      case 'agent_part_start': {
        const coordItem = getOrCreateCoordinatorTimelineItem()
        if (isThinkingPart(event)) {
          coordItem.isThinking = true
          if (event.content) {
            streamedThinking.value += event.content
            coordItem.thinking = (coordItem.thinking || '') + event.content
          }
        } else {
          coordItem.isThinking = false
          if (event.content) {
            streamedText.value += event.content
            coordItem.text += event.content
          }
        }
        break
      }
      case 'agent_part_end':
        if (isThinkingPart(event)) {
          getOrCreateCoordinatorTimelineItem().isThinking = false
        }
        break
      case 'agent_text_delta':
        streamedText.value += event.content
        getOrCreateCoordinatorTimelineItem().text += event.content
        break
      case 'agent_thinking': {
        streamedThinking.value += event.content
        const coordThinking = getOrCreateCoordinatorTimelineItem()
        coordThinking.thinking = (coordThinking.thinking || '') + event.content
        break
      }
      case 'agent_tool_call_start': {
        const coordinatorItem = event.tool_name === 'delegate_task'
          ? getOrCreateCoordinatorTimelineItem(event.tool_call_id || undefined)
          : getOrCreateCoordinatorTimelineItem()
        const existing = activeToolCalls.value.find((t) => t.id === event.tool_call_id)
        if (existing) {
          // Second start event (from call-tools node) — merge args if present
          if (event.tool_args && Object.keys(event.tool_args).length > 0) {
            existing.args = JSON.stringify(event.tool_args, null, 2)
          }
        } else {
          activeToolCalls.value.push({
            id: event.tool_call_id,
            name: event.tool_name,
            args: JSON.stringify(event.tool_args, null, 2),
          })
        }
        appendToolName(coordinatorItem, event.tool_name)
        applyTodoMutation(event.tool_name || '', event.tool_args || {}, event.tool_call_id || '')

        // Pre-create a sub-agent bubble as soon as delegate_task is called
        // so users see immediate feedback (typing dots) before SUBAGENT_START
        // arrives from the worker.
        if (event.tool_name === 'delegate_task' && event.tool_args) {
          const delegateName =
            (event.tool_args as Record<string, unknown>).agent_name
            || (event.tool_args as Record<string, unknown>).agent
            || (event.tool_args as Record<string, unknown>).target_agent
          if (typeof delegateName === 'string' && delegateName.trim()) {
            const taskText =
              event.tool_args.task
              || event.tool_args.prompt
              || event.tool_args.instruction
              || event.tool_args.message
            applyDelegationToCoordinatorItem(
              coordinatorItem,
              delegateName.trim(),
              typeof taskText === 'string' ? taskText : null,
            )
            const cleanDelegateName = delegateName.trim()
            getOrCreateActivity(cleanDelegateName)
            getOrCreateSubAgentTimelineItem(cleanDelegateName)
          }
        }
        break
      }
      case 'agent_tool_call_args': {
        let tc = activeToolCalls.value.find((t) => t.id === event.tool_call_id)
        if (!tc) {
          tc = {
            id: event.tool_call_id,
            name: event.tool_name || 'tool',
            args: '',
          }
          activeToolCalls.value.push(tc)
          appendToolName(getOrCreateCoordinatorTimelineItem(), tc.name)
        }
        tc.args += event.content
        if (tc.name === 'delegate_task') {
          const delegationItem = getOrCreateCoordinatorTimelineItem(event.tool_call_id || undefined)
          const delegation = extractDelegationDetails(tc.args)
          if (delegation.agentName) {
            applyDelegationToCoordinatorItem(delegationItem, delegation.agentName, delegation.taskText)
            getOrCreateActivity(delegation.agentName)
            getOrCreateSubAgentTimelineItem(delegation.agentName)
          }
        }
        break
      }
      case 'agent_tool_result': {
        let tc = activeToolCalls.value.find((t) => t.id === event.tool_call_id)
        if (!tc) {
          tc = {
            id: event.tool_call_id,
            name: event.tool_name || 'tool',
            args: '',
          }
          activeToolCalls.value.push(tc)
          appendToolName(getOrCreateCoordinatorTimelineItem(), tc.name)
        }
        tc.result = event.content
        applyTodoResult(tc.name || event.tool_name || '', event.content || '')
        break
      }
      case 'agent_completed':
        for (const item of conversationTimeline.value) {
          if (!item.done) item.done = true
        }
        if (event.usage) usage.value = event.usage
        if (event.cost_entry) costEntry.value = event.cost_entry
        if (event.citations) citations.value = event.citations
        if (event.result != null) result.value = event.result
        if (event.history) history.value = event.history
        break
      case 'agent_error':
        error.value = event.content
        for (const item of conversationTimeline.value) {
          if (!item.done) item.done = true
        }
        // Clear history so a follow-up request never sends stale
        // history that could contain unprocessed tool calls.
        history.value = []
        break
      default:
        // agent_user_prompt, agent_model_request, agent_part_start, agent_part_end, agent_final_result, agent_code_execution — no UI action needed
        break
    }
    triggerRef(conversationTimeline)
  }

  return {
    streaming,
    runId,
    streamedText,
    streamedThinking,
    activeToolCalls,
    error,
    usage,
    costEntry,
    citations,
    result,
    history,
    subAgentActivities,
    conversationTimeline,
    todoItems,
    startStream,
    resumeStream,
    abort,
  }
}

